import { useState } from 'react'
import { Save, Bell, Shield, Palette, Globe } from 'lucide-react'
import { Button, Card, CardHeader, CardBody, Input, Select } from '../../components/ui'

export default function SettingsPage() {
  const [activeTab, setActiveTab] = useState('general')
  const [isSaving, setIsSaving] = useState(false)

  const handleSave = async () => {
    setIsSaving(true)
    await new Promise(resolve => setTimeout(resolve, 1000))
    setIsSaving(false)
    alert('設定を保存しました')
  }

  const tabs = [
    { id: 'general', label: '一般設定', icon: Globe },
    { id: 'notifications', label: '通知設定', icon: Bell },
    { id: 'security', label: 'セキュリティ', icon: Shield },
    { id: 'appearance', label: '表示設定', icon: Palette },
  ]

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">設定</h1>
          <p className="text-sm text-gray-500 mt-1">システム全般の設定を管理</p>
        </div>
        <Button leftIcon={<Save className="w-4 h-4" />} onClick={handleSave} isLoading={isSaving}>
          設定を保存
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar */}
        <div className="lg:col-span-1">
          <Card>
            <CardBody className="p-2">
              <nav className="space-y-1">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      activeTab === tab.id
                        ? 'bg-primary-50 text-primary-700'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <tab.icon className="w-5 h-5 mr-3" />
                    {tab.label}
                  </button>
                ))}
              </nav>
            </CardBody>
          </Card>
        </div>

        {/* Content */}
        <div className="lg:col-span-3">
          {activeTab === 'general' && (
            <Card>
              <CardHeader>
                <h3 className="font-semibold text-gray-900">一般設定</h3>
              </CardHeader>
              <CardBody className="space-y-6">
                <Input
                  label="システム名"
                  defaultValue="DTL Pro 広告管理システム"
                />
                <Select
                  label="タイムゾーン"
                  options={[
                    { value: 'Asia/Tokyo', label: 'Asia/Tokyo (日本標準時)' },
                    { value: 'UTC', label: 'UTC' },
                  ]}
                  defaultValue="Asia/Tokyo"
                />
                <Select
                  label="言語"
                  options={[
                    { value: 'ja', label: '日本語' },
                    { value: 'en', label: 'English' },
                  ]}
                  defaultValue="ja"
                />
                <div>
                  <label className="form-label">デフォルト広告配信設定</label>
                  <div className="space-y-2 mt-2">
                    <label className="flex items-center">
                      <input type="checkbox" className="form-checkbox" defaultChecked />
                      <span className="ml-2 text-sm">新規店舗で自動配信を有効にする</span>
                    </label>
                    <label className="flex items-center">
                      <input type="checkbox" className="form-checkbox" defaultChecked />
                      <span className="ml-2 text-sm">クリエイティブの自動審査を有効にする</span>
                    </label>
                  </div>
                </div>
              </CardBody>
            </Card>
          )}

          {activeTab === 'notifications' && (
            <Card>
              <CardHeader>
                <h3 className="font-semibold text-gray-900">通知設定</h3>
              </CardHeader>
              <CardBody className="space-y-6">
                <div>
                  <label className="form-label">メール通知</label>
                  <div className="space-y-2 mt-2">
                    <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-sm">キャンペーン予算アラート</span>
                      <input type="checkbox" className="form-checkbox" defaultChecked />
                    </label>
                    <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-sm">クリエイティブ審査完了</span>
                      <input type="checkbox" className="form-checkbox" defaultChecked />
                    </label>
                    <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-sm">配信エラー通知</span>
                      <input type="checkbox" className="form-checkbox" defaultChecked />
                    </label>
                    <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-sm">日次レポート</span>
                      <input type="checkbox" className="form-checkbox" />
                    </label>
                    <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-sm">週次レポート</span>
                      <input type="checkbox" className="form-checkbox" defaultChecked />
                    </label>
                  </div>
                </div>
                <Input
                  label="通知先メールアドレス"
                  type="email"
                  defaultValue="admin@example.com"
                  hint="複数の場合はカンマ区切り"
                />
              </CardBody>
            </Card>
          )}

          {activeTab === 'security' && (
            <Card>
              <CardHeader>
                <h3 className="font-semibold text-gray-900">セキュリティ設定</h3>
              </CardHeader>
              <CardBody className="space-y-6">
                <div>
                  <label className="form-label">パスワードポリシー</label>
                  <div className="space-y-2 mt-2">
                    <label className="flex items-center">
                      <input type="checkbox" className="form-checkbox" defaultChecked />
                      <span className="ml-2 text-sm">最小8文字以上</span>
                    </label>
                    <label className="flex items-center">
                      <input type="checkbox" className="form-checkbox" defaultChecked />
                      <span className="ml-2 text-sm">大文字・小文字を含む</span>
                    </label>
                    <label className="flex items-center">
                      <input type="checkbox" className="form-checkbox" defaultChecked />
                      <span className="ml-2 text-sm">数字を含む</span>
                    </label>
                    <label className="flex items-center">
                      <input type="checkbox" className="form-checkbox" />
                      <span className="ml-2 text-sm">特殊文字を含む</span>
                    </label>
                  </div>
                </div>
                <Select
                  label="セッションタイムアウト"
                  options={[
                    { value: '30', label: '30分' },
                    { value: '60', label: '1時間' },
                    { value: '120', label: '2時間' },
                    { value: '480', label: '8時間' },
                  ]}
                  defaultValue="60"
                />
                <div>
                  <label className="form-label">二要素認証</label>
                  <div className="mt-2 p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-600">二要素認証は現在無効です</p>
                    <Button variant="secondary" size="sm" className="mt-2">
                      二要素認証を有効にする
                    </Button>
                  </div>
                </div>
              </CardBody>
            </Card>
          )}

          {activeTab === 'appearance' && (
            <Card>
              <CardHeader>
                <h3 className="font-semibold text-gray-900">表示設定</h3>
              </CardHeader>
              <CardBody className="space-y-6">
                <Select
                  label="テーマ"
                  options={[
                    { value: 'light', label: 'ライト' },
                    { value: 'dark', label: 'ダーク' },
                    { value: 'system', label: 'システム設定に従う' },
                  ]}
                  defaultValue="light"
                />
                <Select
                  label="一覧表示件数"
                  options={[
                    { value: '10', label: '10件' },
                    { value: '20', label: '20件' },
                    { value: '50', label: '50件' },
                    { value: '100', label: '100件' },
                  ]}
                  defaultValue="20"
                />
                <div>
                  <label className="form-label">ダッシュボード表示</label>
                  <div className="space-y-2 mt-2">
                    <label className="flex items-center">
                      <input type="checkbox" className="form-checkbox" defaultChecked />
                      <span className="ml-2 text-sm">KPIカードを表示</span>
                    </label>
                    <label className="flex items-center">
                      <input type="checkbox" className="form-checkbox" defaultChecked />
                      <span className="ml-2 text-sm">配信推移グラフを表示</span>
                    </label>
                    <label className="flex items-center">
                      <input type="checkbox" className="form-checkbox" defaultChecked />
                      <span className="ml-2 text-sm">キャンペーンランキングを表示</span>
                    </label>
                    <label className="flex items-center">
                      <input type="checkbox" className="form-checkbox" defaultChecked />
                      <span className="ml-2 text-sm">アラートを表示</span>
                    </label>
                  </div>
                </div>
              </CardBody>
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}

